import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { ArrowUpIcon } from "@heroicons/react/outline";
import { Editor } from "@tiptap/core";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useRef, useState } from "react";
import { Prompt } from "react-router";
import TimeAgo from "react-timeago";
import {
  CreatePrivateNoteMutationMutation,
  CreatePrivateNoteMutationMutationVariables,
  DeletePrivateNoteMutationMutation,
  DeletePrivateNoteMutationMutationVariables,
  GetPrivateNotesQueryQuery,
  GetPrivateNotesQueryQueryVariables,
} from "types/graphql-schema";

import { currentUserVar, errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames } from "@helpers/css";
import { compactTimeAgoFormatter, isCommandEnterEvent } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import createPrivateNoteMutation from "./graphql/create-private-note-mutation";
import deletePrivateNoteMutation from "./graphql/delete-private-note-mutation";
import getPrivateNotesQuery from "./graphql/get-private-notes-query";

type PrivateNoteEdge = NonNullable<
  NonNullable<
    NonNullable<GetPrivateNotesQueryQuery["user"]>["privateNotes"]
  >["edges"][number]
>;
type PrivateNoteNode = NonNullable<PrivateNoteEdge["node"]>;

const isEmptyBlock = (node: any): boolean => {
  if (node.type === "paragraph" || node.type === "heading") {
    return !node.content || node.content.length === 0;
  }
  return false;
};

// Cmd+Enter (Tiptap's default Mod-Enter binding on HardBreak) inserts a
// trailing hardBreak inside the last paragraph; strip those too so the saved
// JSON has no trailing line breaks.
const stripTrailingBreaksFromLastBlock = (node: any) => {
  if (!Array.isArray(node?.content)) return;
  while (
    node.content.length > 0 &&
    node.content[node.content.length - 1]?.type === "hardBreak"
  ) {
    node.content.pop();
  }
};

const stripTrailingEmptyBlocks = (value: string | null | undefined) => {
  if (!value) return value;
  try {
    const doc = JSON.parse(value);
    if (!doc?.content || !Array.isArray(doc.content)) return value;
    while (doc.content.length > 0) {
      const last = doc.content[doc.content.length - 1];
      stripTrailingBreaksFromLastBlock(last);
      if (isEmptyBlock(last)) {
        doc.content.pop();
        continue;
      }
      break;
    }
    return JSON.stringify(doc);
  } catch {
    return value;
  }
};

const PrivateNote = ({
  note,
  profileId,
  currentUserId,
}: {
  note: PrivateNoteNode;
  profileId: number;
  currentUserId?: number;
}) => {
  const { ConfirmationDialog, confirm } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete this private note?",
  );

  const client = useApolloClient();
  const [deletePrivateNote] = useMutation<
    DeletePrivateNoteMutationMutation,
    DeletePrivateNoteMutationMutationVariables
  >(deletePrivateNoteMutation, {
    variables: { privateNoteId: note.pk as number },
  });

  const handleDelete = useCallback(async () => {
    const confirmation = await confirm();
    if (!confirmation) return;
    // Filter the note out of the list query immediately so it disappears
    // instantly, then fire the mutation. Snapshot the query first so we can
    // roll the removal back if the mutation fails.
    const snapshot = client.cache.readQuery<
      GetPrivateNotesQueryQuery,
      GetPrivateNotesQueryQueryVariables
    >({ query: getPrivateNotesQuery, variables: { profileId } });
    const connection = snapshot?.user?.privateNotes;
    if (snapshot?.user && connection) {
      client.cache.writeQuery({
        query: getPrivateNotesQuery,
        variables: { profileId },
        data: {
          ...snapshot,
          user: {
            ...snapshot.user,
            privateNotes: {
              ...connection,
              totalCount: Math.max(0, connection.totalCount - 1),
              edges: connection.edges.filter(
                (edge) => edge?.node?.id !== note.id,
              ),
            },
          },
        },
      });
    }
    try {
      await deletePrivateNote();
    } catch {
      // Restore the note so the UI matches the server on failure.
      if (snapshot) {
        client.cache.writeQuery({
          query: getPrivateNotesQuery,
          variables: { profileId },
          data: snapshot,
        });
      }
      errorNotificationVar({
        title: "Error",
        description: "The private note could not be deleted.",
      });
    }
  }, [client, confirm, deletePrivateNote, note.id, profileId]);

  const isCreator = note.creator?.id === currentUserId;

  return (
    <li
      aria-label="private-note"
      className="js-test-private-note group flex items-start gap-2 py-3"
    >
      <ConfirmationDialog />
      <div className="min-w-0 flex-1 text-sm text-gray-800">
        <TextareaWysiwyg
          editable={false}
          value={stripTrailingEmptyBlocks(note.notes)}
          className="[&_p]:!my-0"
        />
      </div>
      <div className="flex shrink-0 items-center gap-1 h-5">
        <span className="whitespace-nowrap text-xs text-gray-500">
          <TimeAgo date={note.created} formatter={compactTimeAgoFormatter} />
        </span>
        {isCreator && (
          <Button
            theme={buttonTheme.iconGray}
            size={ButtonSize.small}
            icon={getTopicflowIcon("close")}
            aria-label="Delete private note"
            className="opacity-0 transition group-hover:opacity-100 focus-within:opacity-100"
            onClick={handleDelete}
          />
        )}
      </div>
    </li>
  );
};

const ProfilePrivateNotes = ({
  profileId,
  className,
  disabled,
  showHeader = false,
}: {
  profileId: number;
  className?: string;
  disabled: boolean;
  showHeader?: boolean;
}) => {
  const currentUser = currentUserVar();
  const [value, setValue] = useState("");
  const editorRef = useRef<Editor | null>(null);

  const { data, loading, fetchMore } = useQuery<
    GetPrivateNotesQueryQuery,
    GetPrivateNotesQueryQueryVariables
  >(getPrivateNotesQuery, {
    variables: { profileId },
  });

  const [createPrivateNote, { loading: saving }] = useMutation<
    CreatePrivateNoteMutationMutation,
    CreatePrivateNoteMutationMutationVariables
  >(createPrivateNoteMutation, {
    onCompleted: () => {
      editorRef.current?.chain().setContent("", { emitUpdate: false }).run();
      setValue("");
    },
    onError: () => {
      errorNotificationVar({
        title: "Error",
        description: "The private note could not be saved.",
      });
    },
    update: (cache, { data: mutationData }) => {
      const newNote = mutationData?.createPrivateNote?.privateNote;
      const existing = cache.readQuery<
        GetPrivateNotesQueryQuery,
        GetPrivateNotesQueryQueryVariables
      >({ query: getPrivateNotesQuery, variables: { profileId } });
      const connection = existing?.user?.privateNotes;
      if (!newNote || !existing?.user || !connection) return;
      cache.writeQuery({
        query: getPrivateNotesQuery,
        variables: { profileId },
        data: {
          ...existing,
          user: {
            ...existing.user,
            privateNotes: {
              ...connection,
              totalCount: connection.totalCount + 1,
              // Notes are ordered newest-first, so prepend.
              edges: [
                { __typename: "PrivateNoteNodeEdge", node: newNote },
                ...connection.edges,
              ],
            },
          },
        },
      });
    },
  });

  const handleSave = useCallback(() => {
    if (disabled || saving) return;
    // Read directly from the editor so we capture any paragraph split that
    // Cmd+Enter inserted before this handler fired.
    const liveValue = editorRef.current
      ? JSON.stringify(editorRef.current.getJSON())
      : value;
    if (isEmptyValue(liveValue)) return;
    const cleaned = stripTrailingEmptyBlocks(liveValue) ?? liveValue;
    createPrivateNote({
      variables: { profileId, notes: cleaned },
      optimisticResponse: {
        __typename: "Mutation",
        createPrivateNote: {
          __typename: "CreatePrivateNoteMutation",
          privateNote: {
            __typename: "PrivateNoteNode",
            id: -moment.utc().valueOf(),
            pk: -1,
            notes: cleaned,
            created: moment.utc().format(),
            creator: currentUser,
          },
        },
      },
    });
  }, [createPrivateNote, currentUser, disabled, profileId, saving, value]);

  const privateNotes = data?.user?.privateNotes;
  const pageInfo = privateNotes?.pageInfo;

  const handleFetchMore = useCallback(() => {
    if (pageInfo?.endCursor) {
      fetchMore({
        variables: { after: pageInfo.endCursor, merge: true },
      });
    }
  }, [fetchMore, pageInfo]);

  const notes = compact(privateNotes?.edges.map((edge) => edge?.node));

  const canSubmit = !disabled && !isEmptyValue(value) && !saving;

  return (
    <div className={classNames("flex h-full flex-col", className)}>
      <Prompt
        when={!isEmptyValue(value)}
        message="Leave page? Changes you made may not be saved."
      />

      {showHeader && (
        <h2 className="pb-6 pt-2 text-left text-lg font-semibold text-gray-900">
          Private Notes
        </h2>
      )}

      {!disabled && (
        <div
          aria-label="private-note-input"
          className={classNames(
            "js-test-private-note-input relative",
            saving && "bg-gray-50 pointer-events-none",
          )}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter saves the note.
            if (isCommandEnterEvent(e)) {
              e.preventDefault();
              handleSave();
            }
          }}
        >
          <TextareaWysiwyg
            ref={editorRef}
            editable
            value={value}
            onChangeValue={(v) => setValue(v)}
            placeholder="Add a private note"
            className="pr-12 [&_p]:!my-0"
          />
          <button
            type="button"
            aria-label="Save private note"
            className={classNames(
              "js-test-save-private-note absolute right-4 top-1.5 h-6 w-6 flex items-center justify-center rounded-full",
              canSubmit
                ? "bg-blue-link text-white hover:bg-blue-800"
                : "bg-gray-100 text-gray-500",
            )}
            disabled={!canSubmit}
            onClick={handleSave}
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
          {saving && (
            <div className="absolute right-12 top-3">
              <Loading mini size="4" />
            </div>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto pt-3">
        {loading && !data ? (
          <Loading>Loading private notes...</Loading>
        ) : (
          <ul aria-label="Private notes" className="divide-y divide-gray-100">
            {notes.map((note) => (
              <PrivateNote
                key={note.id}
                note={note}
                profileId={profileId}
                currentUserId={currentUser.id}
              />
            ))}
          </ul>
        )}
        {pageInfo?.hasNextPage && (
          <div className="flex justify-center pt-2">
            <Button text="Load more" onClick={handleFetchMore} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePrivateNotes;
